/**
 * @file     mortgageBreakdown.ts
 * @purpose  Monthly payment breakdown for the public calculators and listing
 *           pages (P&I, tax, insurance, strata, CMHC), plus the property-tax
 *           estimate and CAD formatters they share.
 */
import { amortizedPayment, canadianPeriodicRate } from './mortgage';
import { mortgageInsurance } from './mortgageInsurance';
import { DEFAULT_PROPERTY_TAX_ANNUAL_RATE } from './housingCost';

export type MortgageInputs = {
  homePrice: number;
  downPayment: number;
  annualInterestRate: number;
  amortizationYears: number;
  annualPropertyTax: number;
  annualInsurance: number;
  monthlyHoa: number;
};

export type MortgageBreakdown = {
  monthlyPrincipalAndInterest: number;
  monthlyPropertyTax: number;
  monthlyInsurance: number;
  monthlyHoa: number;
  monthlyTotal: number;
  totalCost: number;
  totalInterest: number;
  cmhcPremium: number;
  cmhcRequired: boolean;
  effectivePrincipal: number;
  downPaymentPercent: number;
  monthlyInterest: number;
  monthlyPrincipal: number;
};

/**
 * Delegates to core so the premium has one implementation. The local copy this
 * replaced fell back to the top 4% band above 95% LTV, where core correctly
 * returns nothing: no insurer writes that loan, so a premium there was revenue
 * quoted on a mortgage that cannot exist.
 */
export const calculateCMHCPremium = (homePrice: number, downPayment: number): number =>
  mortgageInsurance(homePrice, downPayment).premium;

/** Greater Vancouver blended effective residential property-tax rate, applied to assessed
 *  (here, list) value. Used to estimate annual tax when a listing carries no tax figure. */
export const GREATER_VANCOUVER_PROPERTY_TAX_RATE = DEFAULT_PROPERTY_TAX_ANNUAL_RATE;

/** Ceiling for a believable annual tax figure, as a share of list price. BC effective rates sit
 *  near 0.35%; feed records occasionally carry an assessed value or a typo in TaxAnnualAmount. */
const MAX_PLAUSIBLE_TAX_SHARE_OF_PRICE = 0.07;

/** Feed tax figures are agent-entered and sometimes hold an assessment or a mistyped number.
 *  Returns null when the value cannot be a year of property tax on a home at this price. */
export const plausibleAnnualPropertyTax = (
  taxAnnualAmount: number | null | undefined,
  listPrice: number | null | undefined,
): number | null => {
  if (taxAnnualAmount == null || !Number.isFinite(taxAnnualAmount) || taxAnnualAmount <= 0) return null;
  if (listPrice == null || listPrice <= 0) return taxAnnualAmount;
  return taxAnnualAmount > listPrice * MAX_PLAUSIBLE_TAX_SHARE_OF_PRICE ? null : taxAnnualAmount;
};

/** Estimate annual property tax from home price, rounded to the nearest $100. */
export const estimateAnnualPropertyTax = (homePrice: number): number =>
  homePrice > 0 ? Math.round((homePrice * GREATER_VANCOUVER_PROPERTY_TAX_RATE) / 100) * 100 : 0;

export const calculateMonthlyPayment = (principal: number, annualRate: number, amortizationMonths: number): number => {
  const payment = amortizedPayment(principal, annualRate, amortizationMonths);
  return Math.round(payment * 100) / 100;
};

export const calculateMortgageBreakdown = (inputs: MortgageInputs): MortgageBreakdown => {
  const { homePrice, downPayment, annualInterestRate, amortizationYears, annualPropertyTax, annualInsurance, monthlyHoa } = inputs;
  const downPaymentPercent = homePrice > 0 ? (downPayment / homePrice) * 100 : 0;
  // One call decides both, so "insurance required" and "premium charged" cannot
  // disagree the way they could when each was derived separately.
  const { required: cmhcRequired, premium: cmhcPremium } = mortgageInsurance(homePrice, downPayment);
  const effectivePrincipal = homePrice - downPayment + cmhcPremium;
  const amortizationMonths = amortizationYears * 12;
  const monthlyPrincipalAndInterest = calculateMonthlyPayment(effectivePrincipal, annualInterestRate / 100, amortizationMonths);
  const monthlyPropertyTax = annualPropertyTax / 12;
  const monthlyInsurance = annualInsurance / 12;
  const monthlyTotal = monthlyPrincipalAndInterest + monthlyPropertyTax + monthlyInsurance + monthlyHoa;
  const totalCost = monthlyTotal * amortizationMonths + downPayment;
  const totalInterest = monthlyPrincipalAndInterest * amortizationMonths - effectivePrincipal;

  const round = (n: number) => Math.round(n * 100) / 100;
  let monthlyInterest = 0;
  let monthlyPrincipal = monthlyPrincipalAndInterest;
  if (annualInterestRate > 0 && effectivePrincipal > 0) {
    monthlyInterest = effectivePrincipal * canadianPeriodicRate(annualInterestRate / 100, 12);
    monthlyPrincipal = monthlyPrincipalAndInterest - monthlyInterest;
  }

  return {
    monthlyPrincipalAndInterest: round(monthlyPrincipalAndInterest),
    monthlyPropertyTax: round(monthlyPropertyTax),
    monthlyInsurance: round(monthlyInsurance),
    monthlyHoa: round(monthlyHoa),
    monthlyTotal: round(monthlyTotal),
    totalCost: round(totalCost),
    totalInterest: round(totalInterest),
    cmhcPremium: round(cmhcPremium),
    cmhcRequired,
    effectivePrincipal: round(effectivePrincipal),
    downPaymentPercent: round(downPaymentPercent),
    monthlyInterest: round(monthlyInterest),
    monthlyPrincipal: round(monthlyPrincipal),
  };
};

export const formatCAD = (amount: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

export const formatCADCents = (amount: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
